import { z } from 'zod';
import {
  CONTENT_TYPES,
  MEDIA_KINDS,
  SCALING_MODES,
  SCREEN_ORIENTATIONS,
  TRANSITIONS,
} from './constants.js';
import { MENU_LANGUAGES, menuMediaIds } from './menu.js';
import { MENU_PACK_API } from './menu/pack.js';
import { manifestDesignSchema } from './schemas/design.js';

/**
 * The player manifest: a self-contained description of everything a screen needs in order to
 * play correctly for the foreseeable future, entirely offline.
 *
 * Versioned so that future player releases can negotiate. `contentVersion` is a hash computed
 * from every row that influences this document; the heartbeat returns it so an unchanged
 * screen never downloads the manifest.
 */

export const MANIFEST_SCHEMA_VERSION = 1;

export const timeRangeSchema = z.object({
  start: z.string().regex(/^\d{1,2}:\d{2}$/),
  end: z.string().regex(/^\d{1,2}:\d{2}$/),
});

export const manifestMediaSchema = z.object({
  mediaId: z.string(),
  kind: z.enum(MEDIA_KINDS),
  mimeType: z.string(),
  /** Lower-case hex sha256 of the file. Doubles as the cache key and the integrity check. */
  sha256: z.string().length(64),
  sizeBytes: z.number().int().nonnegative(),
  /** Absolute URL; may be signed and time limited. Re-fetch the manifest if it expires. */
  url: z.string(),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional(),
  durationMs: z.number().int().nonnegative().nullable().optional(),
  /** Optional thumbnail for player-side UI; never required for playback. */
  thumbnailUrl: z.string().nullable().optional(),
  name: z.string().optional(),
});
export type ManifestMedia = z.infer<typeof manifestMediaSchema>;

export const manifestPlaylistItemSchema = z.object({
  id: z.string(),
  position: z.number().int().nonnegative(),
  /**
   * `media` plays one file, `slideshow` nests a slideshow, `playlist` nests a playlist and
   * `menu` shows a menu board.
   */
  itemType: z.enum(['media', 'slideshow', 'playlist', 'menu']),
  mediaId: z.string().nullable().optional(),
  slideshowId: z.string().nullable().optional(),
  playlistId: z.string().nullable().optional(),
  menuId: z.string().nullable().optional(),
  /** For images/slides. Videos use their natural duration unless `durationMs` is forced. */
  durationMs: z.number().int().positive().nullable().optional(),
  transition: z.enum(TRANSITIONS).default('none'),
  transitionMs: z.number().int().nonnegative().default(600),
  scaling: z.enum(SCALING_MODES).default('fit'),
  muted: z.boolean().default(true),
  /** Optional per-item availability window, evaluated with the same scheduling rules. */
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  daysOfWeek: z.array(z.number().int().min(1).max(7)).nullable().optional(),
  timeRanges: z.array(timeRangeSchema).nullable().optional(),
});
export type ManifestPlaylistItem = z.infer<typeof manifestPlaylistItemSchema>;

export const manifestPlaylistSchema = z.object({
  playlistId: z.string(),
  name: z.string(),
  loop: z.boolean().default(true),
  items: z.array(manifestPlaylistItemSchema),
});
export type ManifestPlaylist = z.infer<typeof manifestPlaylistSchema>;

export const manifestSlideshowItemSchema = z.object({
  id: z.string(),
  position: z.number().int().nonnegative(),
  mediaId: z.string(),
  durationMs: z.number().int().positive(),
  transition: z.enum(TRANSITIONS).default('fade'),
  transitionMs: z.number().int().nonnegative().default(600),
  scaling: z.enum(SCALING_MODES).default('fit'),
});
export type ManifestSlideshowItem = z.infer<typeof manifestSlideshowItemSchema>;

export const manifestSlideshowSchema = z.object({
  slideshowId: z.string(),
  name: z.string(),
  loop: z.boolean().default(true),
  items: z.array(manifestSlideshowItemSchema),
});
export type ManifestSlideshow = z.infer<typeof manifestSlideshowSchema>;

export const manifestMenuItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  titleAr: z.string().nullable(),
  description: z.string().nullable(),
  descriptionAr: z.string().nullable(),
  ingredients: z.array(z.string()),
  ingredientsAr: z.array(z.string()),
  price: z.number(),
  discountPrice: z.number().nullable(),
  imageMediaId: z.string().nullable(),
  category: z.string().nullable(),
  // Added with the section-based designs; defaults keep earlier documents valid.
  variants: z
    .array(
      z.object({
        label: z.string(),
        labelAr: z.string().nullable(),
        detail: z.string().nullable(),
        price: z.number(),
      }),
    )
    .default([]),
  calories: z.number().nullable().default(null),
  badge: z.string().nullable().default(null),
  badgeAr: z.string().nullable().default(null),
  sectionId: z.string().nullable().default(null),
});

/** A menu board with its items inlined, so a price change needs nothing but this document. */
export const manifestMenuSchema = z.object({
  menuId: z.string(),
  name: z.string(),
  templateId: z.string(),
  theme: z.object({
    backgroundColor: z.string(),
    surfaceColor: z.string(),
    textColor: z.string(),
    primaryColor: z.string(),
    accentColor: z.string(),
    fontId: z.string(),
    variant: z.string().nullable().default(null),
    backgroundMediaId: z.string().nullable(),
  }),
  settings: z.object({
    title: z.string().nullable(),
    titleAr: z.string().nullable(),
    language: z.enum(MENU_LANGUAGES),
    currency: z.string(),
    pageDurationMs: z.number().int().positive(),
    showDescriptions: z.boolean(),
    showIngredients: z.boolean(),
  }),
  items: z.array(manifestMenuItemSchema),
  sections: z
    .array(
      z.object({
        id: z.string(),
        title: z.string().nullable(),
        titleAr: z.string().nullable(),
        subtitle: z.string().nullable(),
        subtitleAr: z.string().nullable(),
        imageMediaId: z.string().nullable(),
      }),
    )
    .default([]),
  featuredItemId: z.string().nullable().default(null),
  brand: z
    .object({
      name: z.string().nullable(),
      nameAr: z.string().nullable(),
      tagline: z.string().nullable(),
      taglineAr: z.string().nullable(),
      logoMediaId: z.string().nullable(),
    })
    .default({ name: null, nameAr: null, tagline: null, taglineAr: null, logoMediaId: null }),
  decorMediaIds: z.array(z.string()).default([]),
  custom: manifestDesignSchema.nullable().default(null),
});
export type ManifestMenu = z.infer<typeof manifestMenuSchema>;

export const manifestScheduleSchema = z.object({
  scheduleId: z.string(),
  name: z.string(),
  priority: z.number().int(),
  always: z.boolean(),
  contentType: z.enum(CONTENT_TYPES),
  contentId: z.string(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  daysOfWeek: z.array(z.number().int().min(1).max(7)).nullable().optional(),
  timeRanges: z.array(timeRangeSchema).nullable().optional(),
  updatedAt: z.string(),
});
export type ManifestSchedule = z.infer<typeof manifestScheduleSchema>;

export const playerSettingsSchema = z.object({
  heartbeatIntervalSec: z.number().int().positive(),
  contentCheckIntervalSec: z.number().int().positive(),
  proofOfPlayFlushIntervalSec: z.number().int().positive(),
  proofOfPlayBatchSize: z.number().int().positive(),
  maxCacheBytes: z.number().int().positive(),
  downloadConcurrency: z.number().int().positive(),
  screenshotEnabled: z.boolean(),
});
export type PlayerSettings = z.infer<typeof playerSettingsSchema>;

export const manifestMenuPackSchema = z.object({
  /** The pack contract it implements; a player ignores a pack whose `api` it does not know. */
  api: z.number().int(),
  sha256: z.string().length(64),
  sizeBytes: z.number().int().nonnegative(),
  url: z.string(),
});
export type ManifestMenuPack = z.infer<typeof manifestMenuPackSchema>;

export const playerManifestSchema = z.object({
  schemaVersion: z.number().int(),
  /** Hash of everything below; compare with the heartbeat's contentVersion. */
  contentVersion: z.string(),
  generatedAt: z.string(),
  screen: z.object({
    screenId: z.string(),
    name: z.string(),
    orientation: z.enum(SCREEN_ORIENTATIONS),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    scaling: z.enum(SCALING_MODES),
    rotationDegrees: z.number().int(),
  }),
  organization: z.object({ organizationId: z.string(), name: z.string() }),
  location: z
    .object({
      locationId: z.string(),
      name: z.string(),
      /** IANA timezone driving every schedule evaluation on this device. */
      timezone: z.string(),
    })
    .nullable(),
  settings: playerSettingsSchema,
  schedules: z.array(manifestScheduleSchema),
  playlists: z.array(manifestPlaylistSchema),
  slideshows: z.array(manifestSlideshowSchema),
  /** Absent from manifests written before menu boards existed. */
  menus: z.array(manifestMenuSchema).default([]),
  media: z.array(manifestMediaSchema),
  /**
   * The menu designs as a script to download and draw with (see menu/pack.ts), so new designs
   * reach screens without a player release. Absent when there are no menus; players released
   * before packs existed drop the field and keep their built-in designs.
   */
  menuPack: manifestMenuPackSchema.nullable().optional(),
});
export type PlayerManifest = z.infer<typeof playerManifestSchema>;

/** Media ids actually reachable from the manifest's schedules (cycles are handled). */
export function requiredMediaIds(manifest: PlayerManifest): Set<string> {
  const playlists = new Map(manifest.playlists.map((p) => [p.playlistId, p]));
  const slideshows = new Map(manifest.slideshows.map((s) => [s.slideshowId, s]));
  const menus = new Map((manifest.menus ?? []).map((m) => [m.menuId, m]));
  const media = new Set<string>();
  const seenPlaylists = new Set<string>();
  const seenSlideshows = new Set<string>();

  const walkSlideshow = (id: string) => {
    if (seenSlideshows.has(id)) return;
    seenSlideshows.add(id);
    const s = slideshows.get(id);
    if (!s) return;
    for (const item of s.items) media.add(item.mediaId);
  };

  const walkMenu = (id: string) => {
    const m = menus.get(id);
    if (!m) return;
    for (const mediaId of menuMediaIds(m)) media.add(mediaId);
  };

  const walkPlaylist = (id: string) => {
    if (seenPlaylists.has(id)) return;
    seenPlaylists.add(id);
    const p = playlists.get(id);
    if (!p) return;
    for (const item of p.items) {
      if (item.itemType === 'media' && item.mediaId) media.add(item.mediaId);
      else if (item.itemType === 'slideshow' && item.slideshowId) walkSlideshow(item.slideshowId);
      else if (item.itemType === 'playlist' && item.playlistId) walkPlaylist(item.playlistId);
      else if (item.itemType === 'menu' && item.menuId) walkMenu(item.menuId);
    }
  };

  for (const schedule of manifest.schedules) {
    if (schedule.contentType === 'playlist') walkPlaylist(schedule.contentId);
    else if (schedule.contentType === 'slideshow') walkSlideshow(schedule.contentId);
    else if (schedule.contentType === 'media') media.add(schedule.contentId);
    else if (schedule.contentType === 'menu') walkMenu(schedule.contentId);
  }
  return media;
}

/** The manifest media entries required for playback, in a stable order. */
export function requiredMedia(manifest: PlayerManifest): ManifestMedia[] {
  const ids = requiredMediaIds(manifest);
  const files = manifest.media
    .filter((m) => ids.has(m.mediaId))
    .sort((a, b) => (a.mediaId < b.mediaId ? -1 : a.mediaId > b.mediaId ? 1 : 0));
  const pack = usableMenuPack(manifest);
  // The pack is downloaded, verified, kept and protected from eviction exactly like the
  // board's photos, so it travels as one more required file. It is never played: the planner
  // looks files up in `manifest.media`, where it is not.
  if (pack) {
    files.push({
      mediaId: MENU_PACK_MEDIA_ID,
      kind: 'image',
      mimeType: 'text/javascript',
      sha256: pack.sha256,
      sizeBytes: pack.sizeBytes,
      url: pack.url,
    });
  }
  return files;
}

/** The media id the menu pack is cached under. Not a real media row. */
export const MENU_PACK_MEDIA_ID = 'menu-pack';

/** The manifest's menu pack, when it has menus and a pack this player can run. */
export function usableMenuPack(manifest: PlayerManifest): ManifestMenuPack | null {
  const pack = manifest.menuPack;
  if (!pack || pack.api !== MENU_PACK_API || (manifest.menus ?? []).length === 0) return null;
  return pack;
}
